import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.applyQtyStepMode();
    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  getValue() {
    return this.refs.quantityInput.value;
  }

  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  isQtyStepEnabled() {
    return this.dataset.qtyStepEnabled === 'true' || this.refs.quantityInput.dataset.qtyStepEnabled === 'true';
  }

  applyQtyStepMode() {
    if (!this.isQtyStepEnabled()) return;

    const { quantityInput } = this.refs;
    quantityInput.readOnly = true;
    quantityInput.min = '10';
    quantityInput.step = '10';
    quantityInput.dataset.min = '10';

    const currentValue = parseIntOrDefault(quantityInput.value, 10);
    const normalizedValue = currentValue < 10 ? 10 : Math.ceil(currentValue / 10) * 10;
    quantityInput.value = normalizedValue.toString();
  }

  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;
    const qtyStepEnabled = this.isQtyStepEnabled();

    quantityInput.min = qtyStepEnabled ? '10' : min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = qtyStepEnabled ? '10' : step;

    this.applyQtyStepMode();

    const newMin = parseIntOrDefault(quantityInput.min, 1);
    const newStep = parseIntOrDefault(quantityInput.step, 1);
    const effectiveMax = this.getEffectiveMax();

    if (qtyStepEnabled) {
      const normalizedValue = currentValue < 10 ? 10 : Math.ceil(currentValue / 10) * 10;
      const newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, normalizedValue));

      if (newValue !== currentValue) {
        quantityInput.value = newValue.toString();
      }

      this.updateButtonStates();
      return;
    }

    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    return Math.max(max - cartQuantity, min);
  }

  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const minimumValue = this.isQtyStepEnabled() ? 10 : min;

    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= minimumValue;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  getQtyStepValue(value, effectiveMax, stepMultiplier) {
    const minimumValue = 10;
    const nextValue = value + 10 * stepMultiplier;
    return Math.min(effectiveMax ?? Infinity, Math.max(minimumValue, nextValue));
  }

  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = this.isQtyStepEnabled()
      ? this.getQtyStepValue(value, effectiveMax, stepMultiplier)
      : Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const qtyStepEnabled = this.isQtyStepEnabled();
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(qtyStepEnabled ? 10 : min, parseInt(event.target.value) || 0));

    if (qtyStepEnabled) {
      const normalizedValue = quantity < 10 ? 10 : Math.ceil(quantity / 10) * 10;
      quantityInput.value = normalizedValue.toString();
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const minimumValue = this.isQtyStepEnabled() ? 10 : min;

    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(minimumValue, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}